/*
 * Created by Vladimir Shishlyannikov on 03/04/2023.
 * Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 */

import React, { useCallback } from "react";
import { func, object } from "prop-types";
import { CardNumberElement } from "@stripe/react-stripe-js";

// Components
import TruBeTooltip from "../../TruBeTooltip/TruBeTooltip";

// Utils
import { makeCancelable } from "../../../utils";
import { elementOptions } from "../../../constants";

const AUTOHIDE_TIME = 4000;

class CardNumberInputField extends React.Component {
  static propTypes = {
    elementRef: object,
    onChange: func,
  };

  constructor(props) {
    super(props);

    this.inputRef = React.createRef(null);

    this.changeValue = this.changeValue.bind(this);
    this.hideTooltip = this.hideTooltip.bind(this);

    this.state = {
      errorMsg: "",
    };
  }

  changeValue(e) {
    if (e.error && e.elementType === "cardNumber") {
      if (!this.state.isTooltipShow) {
        this.setState({ errorMsg: e.error.message });
        this.showTooltip();
      }
    }

    this.props.onChange && this.props.onChange(e);
  }

  showTooltip() {
    if (this.hideCancelablePromise) {
      return;
    }

    // Auto hide afer 4 sec
    var hidePromise = new Promise((resolve, reject) => {
      this.setState({ isTooltipShow: true });

      setTimeout(() => {
        resolve();
      }, AUTOHIDE_TIME);
    });

    this.hideCancelablePromise = makeCancelable(hidePromise);

    return this.hideCancelablePromise.promise.then(() => {
      this.hideCancelablePromise = null;
      this.hideTooltip();
    });
  }

  hideTooltip() {
    if (this.hideCancelablePromise) {
      this.hideCancelablePromise.cancel();
      this.hideCancelablePromise = null;
    }

    this.setState({ isTooltipShow: false }, () => {
      this.props.onAfterTooltipHide && this.props.onAfterTooltipHide();
    });
  }

  componentWillUnmount() {
    if (this.hideCancelablePromise) {
      this.hideCancelablePromise.cancel();
      this.hideCancelablePromise = null;
    }
  }

  attachRef(node) {
    const ref = this.props.elementRef;

    this.inputRef.current = node;

    if (ref == null) {
      return;
    }

    if (typeof ref === "function") {
      ref(node);

      return;
    }

    if (typeof ref === "object") {
      ref.current = node;
    }
  }

  render() {
    const { className, showRequired, showError, isFormSubmitted } = this.props;
    const { errorMsg } = this.state;

    return (
      <div
        className={`${className ? className + " " : ""} ${
          (showError || showRequired) && isFormSubmitted ? "has-error" : ""
        }
          ${showRequired && isFormSubmitted ? "required" : ""}`}
      >
        <label>
          <div className="tbk-text-small tbk-mb-0.5 tbk-text-black-grey">
            Card number:
          </div>
          <div className="with-tooltip tbk-relative">
            <CardNumberElement
              ref={this.attachRef}
              onChange={this.changeValue}
              options={elementOptions}
            />
            <TruBeTooltip
              className="tbk-text-small tbk-text-center"
              isShow={this.state.isTooltipShow}
              element={this.inputRef.current}
              adjust
              inside
              placement="top"
              onAfterHide={() => this.setState({ errorMsg: "" })}
              onClick={this.hideTooltip}
            >
              {errorMsg}
            </TruBeTooltip>
          </div>
        </label>
      </div>
    );
  }
}

export default CardNumberInputField;
